import { MirrorData, SetMirrorData } from '@type/job-add';
import { useTranslation } from 'next-i18next';
import { Label, RecordInterface, SetFormValues } from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import { ActivationType } from '@components/templates/job-add/activation/form/ActivationType';
import { JobPrice } from '@components/templates/job-add/edit/form/JobPrice';
import { useJobActivationServices } from '@components/templates/job-add/edit/hooks/useJobActivationServices';
import { CheckCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/16/solid';
import { JobWithoutCharge } from '@components/templates/job-add/edit/form/JobWithoutCharge';
import { JobBlueCollarFields } from '@components/templates/job-add/edit/form/JobBlueCollarFields';

/** Component props */
interface JobActivationFormProps {
  locale: Locale;
  record: RecordInterface;
  setFormValues: SetFormValues;
  jobSlug?: string;
  currentPackageName?:string;
  inActivePeriod?: boolean,
  googleApiKey: string;
  apiJobActivationServices?: ApiSchema<'ActivationServices'>;
  initialInterviewAddress?: string;
  initialInterviewDateTime?: string;
  reqMirrorData?: Pick<
  MirrorData,
  'title' | 'vacancies' | 'addresses' | 'salaryMin' | 'salaryMax'
  | 'salaryConfidential' | 'workPlacement' | 'domain' | 'ownApplyUrl' | 'repostingNumber' | 'withoutCharge'
  >;
  setMirrorData?: SetMirrorData;
}

/**
 * @description Job activation services component.
 * - `locale`: The current app locale.
 * - `record`: The form record.
 * - `jobSlug`: The job slug.
 * - `inActivePeriod`: If the job is in an active period.
 * - `apiJobActivationServices`: The server side activation services data.
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData`: The set mirror data function.
 * - `currentPackageName`: Current page name
 */
export const JobActivationServices = (props: JobActivationFormProps) => {
  // Destructure props
  const {
    locale,
    currentPackageName,
    record,
    setFormValues,
    jobSlug,
    inActivePeriod,
    googleApiKey,
    apiJobActivationServices,
    initialInterviewAddress,
    initialInterviewDateTime,
    reqMirrorData,
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Job activation services hook
  // *************************
  const {
    hasInteracted,
    lastSelectedIdentifier,
    jobActivationItems,
    selectedIndex,
    selectedActivation,
    setSelectedActivation,
    isValidating,
    benefitIsBold,
  } = useJobActivationServices({
    locale,
    setFormValues,
    jobSlug,
    inActivePeriod,
    apiJobActivationServices,
    reqMirrorData,
    setMirrorData,
  });

  if (inActivePeriod) {
    return (
      <>
        <div className="mb-4 border-b border-dashed pb-4">
          <span className="mb-1 inline-block px-3.5 text-sm font-bold text-ink-medium ">
            {' '}
            {t('global.current.package')}
          </span>
          <div className="ml-4 flex w-fit items-center justify-center gap-2 rounded-full border border-dashed border-secondary px-4 py-1">
            <CheckIcon className="size-5 fill-secondary" />
            <span>{currentPackageName}</span>
          </div>
        </div>

        {selectedActivation?.identifier === ('blue-fix' as string)
          && (
            <div className="mb-4 px-5">
              <JobBlueCollarFields
                record={record}
                setFormValues={setFormValues}
                googleApiKey={googleApiKey}
                isEditMode={!!jobSlug}
                inActivePeriod={inActivePeriod}
                initialInterviewAddress={initialInterviewAddress}
                initialInterviewDateTime={initialInterviewDateTime}
                prefillAddress={reqMirrorData?.addresses?.[0]}
              />
            </div>
          )}
      </>
    );
  }

  // Render component
  // *************************
  return (
    <>
      {/* Choose activation type */}
      <Label
        required
        as="div"
        description={t('job.add.extra.promo.agent')}
        descriptionClass="font-semibold px-5 !my-2 text-ink-medium"
      >
        <div className="relative">
          {/* Loading spinner */}
          {isValidating && (
          <div className="absolute right-2 top-1/2 z-1 -translate-y-1/2 bg-surface p-1">
            <TailwindSpinner className="size-5 text-secondary" />
          </div>
          )}

          {/* Dropdown */}
          <div className={isValidating ? 'pointer-events-none relative opacity-50' : ''}>
            <ActivationType
              disabled={inActivePeriod}
              record={record}
              jobActivationItems={jobActivationItems}
              initialIndex={selectedIndex}
              setSelectedActivation={setSelectedActivation}
              setMirrorData={setMirrorData}
              hasInteracted={hasInteracted}
              lastSelectedIdentifier={lastSelectedIdentifier}
            />
          </div>
        </div>
      </Label>

      {/* Blue Collar Fields */}
      {selectedActivation?.identifier === ('blue-fix' as string)
        && (
          <JobBlueCollarFields
            record={record}
            setFormValues={setFormValues}
            googleApiKey={googleApiKey}
            isEditMode={!!jobSlug}
            inActivePeriod={inActivePeriod}
            initialInterviewAddress={initialInterviewAddress}
            initialInterviewDateTime={initialInterviewDateTime}
            prefillAddress={reqMirrorData?.addresses?.[0]}
          />
        )}

      {/* Choice benefits */}
      <div className="mt-5 divide-y divide-dashed px-4 sm:px-5">
        {selectedActivation?.benefits?.map((benefit, index) => {
          // Subscription benefits are a string array, while bundle benefits are an object array
          const title = typeof benefit === 'object' ? benefit.title : benefit;
          return (
            <div
              key={title}
              className={`flex gap-2 py-2${benefitIsBold(index, selectedActivation.type) ? ' font-bold' : ''}`}
            >
              {benefitIsBold(index, selectedActivation.type)
                ? <CheckCircleIcon className="size-5 shrink-0 text-secondary" />
                : <PlusCircleIcon className="size-5 shrink-0 text-secondary" />}
              {title}
            </div>
          );
        })}
      </div>

      {!inActivePeriod && selectedActivation?.identifier === ('standard-fix' as string)
        && (
          <JobWithoutCharge
            setMirrorData={setMirrorData}
            setFormValues={setFormValues}
            record={record}
          />
        )}

      <JobPrice
        locale={locale}
        record={record}
        jobSlug={jobSlug}
        packageId={selectedActivation?.packageId}
        activationPeriod={selectedActivation?.activationPeriodInfo ?? jobActivationItems[0]?.activationPeriodInfo}
        reqMirrorData={reqMirrorData}
        setMirrorData={setMirrorData}
        removeRepostingFromSubmit={false}
        inActivePeriod={inActivePeriod}
        selectedActivation={selectedActivation}
      />
    </>
  );
};
